import { useEffect, useMemo, useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { url } from "@/lib/basePath";
import { toastApiError } from "@/lib/toast";
import { formatDateTime } from "@/utils/dates";
import { partnerForms, type PartnerLink } from "@/features/onboarding/api/onboarding.api";
import { PartnerRegenerateLinkButton } from "@/features/onboarding/components/RegenerateLinkButton";

export function PartnerLinkField({
  userId,
  userName,
  label = "Partner form link",
}: {
  userId: string;
  userName?: string | null;
  label?: string;
}) {
  const [path, setPath] = useState("");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    partnerForms.link(userId)
      .then((r) => { if (!cancelled) apply(r); })
      .catch((e) => { if (!cancelled) toastApiError(e, "Couldn't build the partner link."); });
    return () => { cancelled = true; };
  }, [userId]);

  function apply(r: PartnerLink) {
    setPath(r.path);
    setExpiresAt(r.expiresAt ?? null);
    setCopied(false);
  }

  const link = useMemo(() => (path ? url(path) : ""), [path]);

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't reach the clipboard — select the link and copy it manually.");
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="partnerFormLink">{label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          id="partnerFormLink"
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 bg-muted font-mono text-slate-700"
        />
        <div className="flex shrink-0 items-center gap-2">
          <PartnerRegenerateLinkButton userId={userId} userName={userName} onDone={apply} label="Regenerate link" />
          <Button type="button" variant="outline" onClick={copy} disabled={!link} className="whitespace-nowrap">
            <Copy />
            {copied ? "Copied ✓" : "Copy link"}
          </Button>
          <Button asChild variant="outline" disabled={!link} className="whitespace-nowrap">
            <a href={link || undefined} target="_blank" rel="noreferrer">
              <ExternalLink />
              Open link
            </a>
          </Button>
        </div>
      </div>
      {link && expiresAt && (
        <p className="text-xs text-muted-foreground">
          Link stops working on{" "}
          {formatDateTime(expiresAt, { year: "numeric", month: "short", day: "numeric" })}
          , then a new one is issued automatically.
        </p>
      )}
    </div>
  );
}
